import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { Spin, Result } from 'antd';
import AdminLayout from '../components/AdminLayout';
import { ADMIN_PATH_PREFIX, getDefaultAdminPath } from '../config/userMenu';
import { readUserInfo } from '../utils/userInfo';

import HomePage from '../pages/HomePage/homePage';

// 页面懒加载：按需生成 chunk，显著减小首屏体积
const BrandBuildingPage = lazy(() => import('../pages/BrandBuildingPage'));
const InspectionInstitutionPage = lazy(() => import('../pages/InspectionInstitutionPage'));
const CertificationPage = lazy(() => import('../pages/CertificationPage'));
const MeasurementPage = lazy(() => import('../pages/MeasurementPage'));
const StandardizationPage = lazy(() => import('../pages/StandardizationPage'));
const IntellectualPropertyPage = lazy(() => import('../pages/IntellectualPropertyPage'));
const EquipmentSharingPage = lazy(() => import('../pages/EquipmentSharingPage'));
const VideoTrainingPage = lazy(() => import('../pages/VideoTrainingPage'));
const CoursePlayerPage = lazy(() => import('../pages/CoursePlayerPage'));
const TrainingListPage = lazy(() => import('../pages/TrainingListPage'));
const PublicCourseListPage = lazy(() => import('../pages/PublicCourseListPage'));
const CourseDetailPage = lazy(() => import('../pages/CourseDetailPage'));
const VideoPlayerPage = lazy(() => import('../pages/VideoPlayerPage'));
const KnowledgeList = lazy(() => import('../pages/KnowledgeList'));
const ExpertList = lazy(() => import('../pages/ExpertList'));
const CaseList = lazy(() => import('../pages/CaseList'));
const InstitutionList = lazy(() => import('../pages/InstitutionList'));
const KnowledgeDetail = lazy(() => import('../pages/KnowledgeDetail'));
const ExpertDetail = lazy(() => import('../pages/ExpertDetail'));
const CaseDetail = lazy(() => import('../pages/CaseDetail/index'));
const InstitutionDetail = lazy(() => import('../pages/InstitutionDetail'));
const ConsultationCreate = lazy(() => import('../pages/ConsultationCreate'));
const MyConsultations = lazy(() => import('../pages/MyConsultations'));
const ConsultationDetail = lazy(() => import('../pages/ConsultationDetail'));
const MyTrainingList = lazy(() => import('../pages/MyTrainingList'));
const MyFavorites = lazy(() => import('../pages/MyFavorites'));
const MyMessages = lazy(() => import('../pages/MyMessages'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const AccountSettings = lazy(() => import('../pages/AccountSettings'));
const ExpertConsultations = lazy(() => import('../pages/ExpertConsultations'));
const DemoTest = lazy(() => import('../pages/DemoTest'));
const MyExamList = lazy(() => import('../pages/MyExamList'));
const ExamRecords = lazy(() => import('../pages/ExamRecords'));
const ExamTaking = lazy(() => import('../pages/ExamTaking'));
const ExamDetail = lazy(() => import('../pages/ExamDetail'));
const OrganizationStaff = lazy(() => import('../pages/OrganizationStaff'));
const OrganizationLaboratory = lazy(() => import('../pages/OrganizationLaboratory'));
const OrganizationQualityCert = lazy(() => import('../pages/OrganizationQualityCert'));
const EvaluationPage = lazy(() => import('../pages/EvaluationPage'));
const MaterialManagement = lazy(() => import('../pages/MaterialManagement'));
const DeviceManagement = lazy(() => import('../pages/DeviceManagement'));
const SampleManagement = lazy(() => import('../pages/SampleManagement'));
const ProjectManagement = lazy(() => import('../pages/ProjectManagement'));
const CertificationProject = lazy(() => import('../pages/CertificationProject'));
const OpenMiniProgram = lazy(() => import('../pages/OpenMiniProgram'));
const ViewReport = lazy(() => import('../pages/ViewReport'));
const ReportReview = lazy(() => import('../pages/ReportReview'));
const SupportPage = lazy(() => import('../pages/Support'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));

// 路由级加载占位
const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <Spin size="large" />
  </div>
);

/**
 * 前台业务路由表
 * 前台 Switch 与管理中心内嵌 Switch 复用同一份定义：
 * 管理中心会把 /admin 前缀去掉后再用它匹配，因此详情页等子路由无需重复维护
 */
const renderAppRoutes = () => [
  <Route key="home" exact path="/" component={HomePage} />,

  <Route key="institutions" exact path="/institutions" component={InstitutionList} />,
  <Route key="institution-detail" path="/institutions/:id" component={InstitutionDetail} />,

  <Route key="knowledge" exact path="/knowledge" component={KnowledgeList} />,
  <Route key="knowledge-detail" path="/knowledge/:id" component={KnowledgeDetail} />,

  <Route key="experts" exact path="/experts" component={ExpertList} />,
  <Route key="expert-detail" path="/experts/:id" component={ExpertDetail} />,

  <Route key="cases" exact path="/cases" component={CaseList} />,
  <Route key="case-detail" path="/cases/:id" component={CaseDetail} />,

  <Route key="brand-building" path="/brand-building" component={BrandBuildingPage} />,
  <Route key="inspection-institution" path="/inspection-institution" component={InspectionInstitutionPage} />,
  <Route key="certification" path="/certification" component={CertificationPage} />,
  <Route key="measurement" path="/measurement" component={MeasurementPage} />,
  <Route key="standardization" path="/standardization" component={StandardizationPage} />,
  <Route key="intellectual-property" path="/intellectual-property" component={IntellectualPropertyPage} />,
  <Route key="equipment-sharing" path="/equipment-sharing" component={EquipmentSharingPage} />,

  <Route key="video-training" exact path="/video-training" component={VideoTrainingPage} />,
  <Route key="video-training-list" path="/video-training/training-list" component={TrainingListPage} />,
  <Route
    key="video-training-public-list"
    path="/video-training/public-course-list"
    component={PublicCourseListPage}
  />,
  <Route
    key="course-player"
    path="/video-training/course/player/:courseId/:chapterId"
    component={CoursePlayerPage}
  />,
  <Route key="course-detail" path="/video-training/course/:id" component={CourseDetailPage} />,
  <Route key="video-player" path="/video-training/video/player/:id" component={VideoPlayerPage} />,

  <Route key="consultation-create" path="/consultation/create/:expertId" component={ConsultationCreate} />,
  <Route key="consultation-detail" path="/consultation/:id" component={ConsultationDetail} />,
  <Route key="my-consultations" exact path="/my-consultations" component={MyConsultations} />,
  <Route key="expert-consultations" exact path="/expert-consultations" component={ExpertConsultations} />,
  <Route key="my-trainings" exact path="/my-trainings" component={MyTrainingList} />,
  <Route key="my-favorites" exact path="/my-favorites" component={MyFavorites} />,
  <Route key="my-messages" exact path="/my-messages" component={MyMessages} />,
  <Route key="profile" exact path="/profile" component={ProfilePage} />,
  <Route key="account-settings" exact path="/account-settings" component={AccountSettings} />,
  <Route key="organization-staff" exact path="/organization-staff" component={OrganizationStaff} />,
  <Route
    key="organization-laboratory"
    exact
    path="/organization-laboratory"
    component={OrganizationLaboratory}
  />,
  <Route
    key="organization-quality-cert"
    exact
    path="/organization-quality-cert"
    component={OrganizationQualityCert}
  />,
  <Route key="material-management" exact path="/material-management" component={MaterialManagement} />,
  <Route key="device-management" exact path="/device-management" component={DeviceManagement} />,
  <Route key="sample-management" exact path="/sample-management" component={SampleManagement} />,
  <Route key="project-management" exact path="/project-management" component={ProjectManagement} />,
  <Route key="certification-project" exact path="/certification-project" component={CertificationProject} />,
  <Route key="evaluation" exact path="/evaluation" component={EvaluationPage} />,

  <Route key="my-exam-list" exact path="/my-exam-list" component={MyExamList} />,
  <Route key="exam-records" exact path="/exam-records" component={ExamRecords} />,
  <Route key="exam-taking" path="/exam-taking/:paperId" component={ExamTaking} />,
  <Route key="exam-detail" path="/exam-detail/:id" component={ExamDetail} />,
  <Route key="demo-test" path="/demoTest" component={DemoTest} />,
  <Route key="open-mini-program" path="/open-mini-program" component={OpenMiniProgram} />,
  <Route key="view-report" path="/viewReport/:reportId" component={ViewReport} />,
  <Route key="report-review" exact path="/report-review" component={ReportReview} />,
  <Route key="support" exact path="/support" component={SupportPage} />,
  <Route key="reset-password" exact path="/reset-password" component={ResetPassword} />,
];

/**
 * 管理中心子路由
 * 把 /admin 前缀去掉后交给前台路由表匹配，页面内的二级跳转（详情、考试等）也能留在管理中心内
 */
const AdminRoutes = () => {
  const location = useLocation();
  const innerPathname = location.pathname.slice(ADMIN_PATH_PREFIX.length) || '/';

  if (innerPathname === '/') {
    // 按登录身份进入各自的首个菜单（个人/专家 → 个人中心，机构 → 从业人员）
    return <Redirect to={`${ADMIN_PATH_PREFIX}${getDefaultAdminPath(readUserInfo())}`} />;
  }

  const innerLocation = { ...location, pathname: innerPathname };

  return (
    <Switch location={innerLocation}>
      {renderAppRoutes()}
      <Route
        key="admin-not-found"
        render={() => (
          <Result status="404" title="页面不存在" subTitle="该功能模块暂未开放或访问路径有误" />
        )}
      />
    </Switch>
  );
};

const AppRouter = () => (
  <Router>
    <Suspense fallback={<PageLoading />}>
      <Switch>
        {renderAppRoutes()}

        {/* 管理中心：左侧菜单 + 右侧内容，支持一键切回前台网站 */}
        <Route
          path="/admin"
          render={() => (
            <AdminLayout>
              <AdminRoutes />
            </AdminLayout>
          )}
        />
      </Switch>
    </Suspense>
  </Router>
);

export default AppRouter;
